import { createContext, useContext, useState, useMemo, ReactNode } from 'react'
import { ThemeProvider as MuiThemeProvider, CssBaseline } from '@mui/material'
import { getTheme } from './theme'
import { usePreferences } from './preferences/PreferencesContext'

type ThemeMode = 'dark' | 'light'

interface ThemeContextValue {
  mode: ThemeMode
  toggleMode: () => void
  setMode: (mode: ThemeMode) => void
}

const ThemeContext = createContext<ThemeContextValue>({
  mode: 'light',
  toggleMode: () => {},
  setMode: () => {},
})

export const useThemeMode = () => useContext(ThemeContext)

export function AppThemeProvider({ children }: { children: ReactNode }) {
  // Pull the kid-mode flag from preferences so the theme switches without
  // any per-page wiring. This MUST be the same source of truth as
  // KidModeContext — both read prefs.kidMode — otherwise theme and routing
  // could disagree on edge frames.
  const { prefs } = usePreferences()
  const [mode, setModeState] = useState<ThemeMode>(() => {
    // Resolution order:
    //   1. Explicit user choice persisted to localStorage (always wins).
    //   2. OS-level `prefers-color-scheme: dark` so dark-mode users don't
    //      get blasted with light theme on first load.
    //   3. Hard fallback to 'light'.
    try {
      const saved = localStorage.getItem('qlearn-theme-mode')
      if (saved === 'light' || saved === 'dark') return saved
    } catch {
      /* private mode / quota — fall through */
    }
    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
      if (window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark'
    }
    return 'light'
  })

  const toggleMode = () => {
    setModeState((prev) => {
      const next = prev === 'dark' ? 'light' : 'dark'
      try { localStorage.setItem('qlearn-theme-mode', next) } catch { /* private mode */ }
      return next
    })
  }

  const setMode = (next: ThemeMode) => {
    setModeState(next)
    try { localStorage.setItem('qlearn-theme-mode', next) } catch { /* private mode */ }
  }

  const theme = useMemo(() => getTheme(mode, prefs.kidMode), [mode, prefs.kidMode])

  return (
    <ThemeContext.Provider value={{ mode, toggleMode, setMode }}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  )
}
